import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

export function useCommunityStats(userId) {
  const [todayCount, setTodayCount] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [challengeCount, setChallengeCount] = useState(0);      // total dias escritos pela comunidade
  const [challengeParticipants, setChallengeParticipants] = useState(0); // nº de participantes
  const [userParticipating, setUserParticipating] = useState(false);
  const [userContribution, setUserContribution] = useState(0);  // dias que o usuário contribuiu
  const [userCheckedInToday, setUserCheckedInToday] = useState(false);
  const [weeklyPrompt, setWeeklyPrompt] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userOptedIn, setUserOptedIn] = useState(false);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];

    // 1. Today's community count
    const { data: statsData } = await supabase
      .from('daily_community_stats')
      .select('completions_count')
      .eq('date', today)
      .single();
    setTodayCount(statsData?.completions_count ?? 0);

    // 2. Active challenge
    const { data: challengeData } = await supabase
      .from('challenges')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    setChallenge(challengeData || null);

    if (challengeData) {
      // 3a. Número de participantes
      const { count: participantCount } = await supabase
        .from('challenge_participation')
        .select('*', { count: 'exact', head: true })
        .eq('challenge_id', challengeData.id);
      setChallengeParticipants(participantCount || 0);

      // 3b. Total de dias escritos por todos os participantes no período
      const { data: logsData } = await supabase
        .from('daily_logs')
        .select('user_id', { count: 'exact' })
        .eq('completed', true)
        .gte('date', challengeData.start_date)
        .lte('date', challengeData.end_date);
      // Filtra apenas participantes do desafio
      if (logsData) {
        const { data: participantIds } = await supabase
          .from('challenge_participation')
          .select('user_id')
          .eq('challenge_id', challengeData.id);
        const ids = (participantIds || []).map(p => p.user_id);
        const totalDays = logsData.filter(l => ids.includes(l.user_id)).length;
        setChallengeCount(totalDays);
      }

      // 3c. Status do usuário no desafio
      if (userId) {
        const { data: participationData } = await supabase
          .from('challenge_participation')
          .select('*')
          .eq('challenge_id', challengeData.id)
          .eq('user_id', userId)
          .single();
        setUserParticipating(!!participationData);

        // Dias que o usuário contribuiu no período
        const { data: userLogs } = await supabase
          .from('daily_logs')
          .select('date')
          .eq('user_id', userId)
          .eq('completed', true)
          .gte('date', challengeData.start_date)
          .lte('date', challengeData.end_date);
        setUserContribution(userLogs?.length || 0);

        // Verificar se já fez check-in hoje
        const todayLog = (userLogs || []).find(l => l.date === today);
        setUserCheckedInToday(!!todayLog);
      }
    }

    // 4. Weekly prompt
    const weekStart = getWeekStart();
    const { data: promptData } = await supabase
      .from('weekly_prompts')
      .select('*')
      .eq('week_start', weekStart)
      .single();
    setWeeklyPrompt(promptData || null);

    // 5. Leaderboard mensal
    const { data: lbData } = await supabase
      .from('leaderboard_monthly')
      .select('*')
      .limit(10);
    setLeaderboard(lbData || []);

    // 6. User opt-in + posição no ranking
    if (userId) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('leaderboard_opt_in')
        .eq('id', userId)
        .single();
      setUserOptedIn(profileData?.leaderboard_opt_in ?? false);

      // Posição do usuário (mesmo se não estiver no top 10)
      const { data: rankData } = await supabase
        .from('leaderboard_monthly')
        .select('rank, points_this_month, display_name, avatar_emoji, user_id')
        .eq('user_id', userId)
        .single();
      setUserRank(rankData || null);
    }

    if (mountedRef.current) setLoading(false);
  }, [userId]);

  // Realtime subscription for today's count
  useEffect(() => {
    fetchAll();

    const today = new Date().toISOString().split('T')[0];
    const channel = supabase
      .channel('daily-logs-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'daily_logs',
          filter: `date=eq.${today}`,
        },
        () => {
          setTodayCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchAll]);

  const joinChallenge = async () => {
    if (!userId || !challenge) return;
    await supabase
      .from('challenge_participation')
      .upsert({ challenge_id: challenge.id, user_id: userId });
    setUserParticipating(true);
    setChallengeParticipants(prev => prev + 1);
    // Recalcular contribuição do usuário
    const { data: userLogs } = await supabase
      .from('daily_logs')
      .select('date')
      .eq('user_id', userId)
      .eq('completed', true)
      .gte('date', challenge.start_date)
      .lte('date', challenge.end_date);
    const contribution = userLogs?.length || 0;
    setUserContribution(contribution);
    setChallengeCount(prev => prev + contribution);
    const today = new Date().toISOString().split('T')[0];
    setUserCheckedInToday(!!(userLogs || []).find(l => l.date === today));
  };

  const toggleOptIn = async () => {
    if (!userId) return;
    const next = !userOptedIn;
    await supabase
      .from('profiles')
      .update({ leaderboard_opt_in: next })
      .eq('id', userId);
    setUserOptedIn(next);
    const { data: lbData } = await supabase
      .from('leaderboard_monthly')
      .select('*')
      .limit(10);
    setLeaderboard(lbData || []);
  };

  return {
    todayCount,
    challenge,
    challengeCount,
    challengeParticipants,
    userParticipating,
    userContribution,
    userCheckedInToday,
    weeklyPrompt,
    leaderboard,
    userOptedIn,
    userRank,
    loading,
    joinChallenge,
    toggleOptIn,
    refresh: fetchAll,
  };
}

function getWeekStart() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day;
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().split('T')[0];
}
